import { useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

const ANIM_URL_DESKTOP =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_solar-lamp-builder/artifacts/4giym8ap_Anim.json";
const ANIM_URL_MOBILE =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_solar-lamp-builder/artifacts/poiyfozs_Anim-Phone.json";
const MOBILE_MAX_WIDTH = 640;

// Scroll length (in viewport heights) over which the animation plays out.
const ANIM_SPAN_VH = 1.5;

export default function HeroLottie() {
  const reduce = useReducedMotion();
  const [data, setData] = useState(null);
  const lottieRef = useRef(null);

  const { scrollY } = useScroll();
  const [vh, setVh] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 800
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_MAX_WIDTH : false
  );

  useEffect(() => {
    const onResize = () => {
      setVh(window.innerHeight);
      setIsMobile(window.innerWidth <= MOBILE_MAX_WIDTH);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Load the appropriate Lottie JSON for the current breakpoint.
  useEffect(() => {
    let cancelled = false;
    setData(null);
    fetch(isMobile ? ANIM_URL_MOBILE : ANIM_URL_DESKTOP)
      .then((r) => r.json())
      .then((json) => {
        if (!cancelled) setData(json);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isMobile]);

  const animEnd = vh * ANIM_SPAN_VH;
  const progress = useTransform(scrollY, [0, animEnd], [0, 1], { clamp: true });
  const hintOpacity = useTransform(progress, [0, 0.06], [1, 0]);

  // Drive the Lottie frame from scroll progress.
  useEffect(() => {
    if (!data) return;
    const totalFrames = (data.op ?? 90) - (data.ip ?? 0);
    const unsubscribe = progress.on("change", (p) => {
      const inst = lottieRef.current;
      if (!inst) return;
      const frame = (reduce ? 0 : p) * totalFrames;
      inst.goToAndStop(frame, true);
    });
    return unsubscribe;
  }, [data, progress, reduce]);

  return (
    <section className="relative w-full" data-testid="hero-driver">
      {/* Spacer that reserves scroll range for the fixed overlay. */}
      <div style={{ height: `${100 + (ANIM_SPAN_VH - 1) * 100}vh` }} />

      <div
        className="pointer-events-none fixed inset-0 z-0 w-full h-[100svh] overflow-hidden bg-[#FBF6E7]"
        data-testid="hero-scene"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          {data ? (
            <div className="w-full h-full origin-center scale-[1.35]">
              <Lottie
                lottieRef={lottieRef}
                animationData={data}
                autoplay={false}
                loop={false}
                renderer="svg"
                rendererSettings={{ preserveAspectRatio: "xMidYMid meet" }}
                className="w-full h-full"
                data-testid="hero-lottie"
              />
            </div>
          ) : null}
        </div>

        <motion.div
          aria-hidden
          className="absolute bottom-[max(env(safe-area-inset-bottom,0px),24px)] left-1/2 -translate-x-1/2 font-hand text-[var(--accent)] text-xl select-none"
          style={{ opacity: hintOpacity }}
          data-testid="hero-scroll-hint"
        >
          scroll to begin ↓
        </motion.div>
      </div>
    </section>
  );
}
